import { rem } from "@certn/cascadia";
import {
  CascadiaColor,
  CascadiaMiniIcon,
  CascadiaShadow,
  CascadiaShape,
  CascadiaSpacing,
  CascadiaStandardIcon,
  CascadiaStyle,
  shapes,
  spacing,
} from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { RefObject, useRef } from "react";
import { Button, ButtonProps as AriaButtonProps } from "react-aria-components";

import { IconButtonProps } from "./_IconButton";
import { BUTTON_SIZES } from "./constants";
import { ButtonSize } from "./types";
import { Icon } from "../../foundations/icons/Icon/Icon";
import { Text } from "../../foundations/typography/Text";
import { useColors } from "../../hooks/useColors";
import { Loader } from "../loaders/loading/Loader";
import { accessibleTouchSize, useGetAllFocusShadows } from "../utils";

export interface ButtonProps extends AriaButtonProps {
  border?: CascadiaColor;
  color: CascadiaColor;
  fill?: CascadiaColor;
  focusStyle?: CascadiaStyle;
  hoverFill?: CascadiaColor;
  iconColor?: CascadiaColor;
  shadow?: CascadiaShadow;
  shape?: CascadiaShape;
  size?: ButtonSize;
  fullWidth?: boolean;
  leftIcon?: CascadiaMiniIcon | CascadiaStandardIcon;
  rightIcon?: CascadiaMiniIcon | CascadiaStandardIcon;
  buttonRef?: RefObject<HTMLButtonElement>;
  isLoading?: boolean;
  loaderColor?: CascadiaColor;
  testID?: string;
  iconSize?: CascadiaSpacing;
  iconFillOverride?: boolean;
  iconType?: "mini" | "standard" | "custom" | undefined;
}

export function _Button(props: ButtonProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const {
    border,
    color,
    fill,
    hoverFill,
    shape = "border-radius-2",
    isLoading,
    buttonRef = ref,
    size = "medium",
    children,
    testID = "button",
    fullWidth,
    leftIcon,
    rightIcon,
    type = "button",
    iconType = "mini",
    ...buttonProps
  } = props;
  const colors = useColors();
  const sizeTokens = BUTTON_SIZES[size];
  const boxShadow = border ? `inset 0 0 0 1px ${colors[border]}` : undefined;
  const getAllFocusShadows = useGetAllFocusShadows();

  return (
    <Button
      isDisabled={props.isDisabled || props.isLoading}
      {...buttonProps}
      type={type}
      className={css(
        styles.button,
        getSizeStyles({
          size,
          fullWidth,
          leftIcon,
          rightIcon,
          children,
        }),
        css(
          {
            background: fill ? colors[fill] : "transparent",
            border: "none",
            outline: "none",
            overflow: "hidden",
            color: color ? colors[color] : undefined,
            borderRadius: shapes[shape],
            "&:focus-within": getAllFocusShadows(boxShadow),
            boxShadow,
            "&:hover:enabled": {
              "> span": {
                color: "undefined",
              },
              background: hoverFill ? colors[hoverFill] : "undefined",
            },
            "&:disabled": {
              opacity: isLoading ? 1 : 0.6,
              cursor: isLoading ? "progress" : "not-allowed",
            },
          },
          isLoading && {
            "span, > i": { color: "transparent", fill: "transparent" },
          }
        ),
        styles.button
      )}
      data-testid={testID}
      ref={buttonRef}
    >
      {(renderProps) => {
        return (
          <>
            {props.leftIcon && (
              <Icon
                type={iconType}
                name={props.leftIcon}
                color={props.iconColor || props.color}
                size={props.iconSize || sizeTokens.iconSize}
                fillOverride={props.iconFillOverride}
              />
            )}
            <Text style={sizeTokens.textStyle} color={props.color}>
              {typeof children === "function"
                ? children(renderProps)
                : children}
            </Text>
            {props.rightIcon && (
              <Icon
                type={iconType}
                name={props.rightIcon}
                color={props.iconColor || props.color}
                size={props.iconSize || sizeTokens.iconSize}
                fillOverride={props.iconFillOverride}
              />
            )}
            {props.isLoading && (
              <Loader
                overlay
                size="medium"
                color={props.loaderColor || props.color}
              />
            )}
          </>
        );
      }}
    </Button>
  );
}

const styles = {
  button: css({
    position: "relative",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:after": accessibleTouchSize,
  }),
};

const getSizeStyles = ({
  size = "medium",
  fullWidth,
  leftIcon,
  rightIcon,
  children,
}: Partial<ButtonProps & IconButtonProps>) => {
  const sizeTokens = BUTTON_SIZES[size];
  return {
    paddingLeft:
      leftIcon && children
        ? sizeTokens.paddingIcon
        : sizeTokens.paddingHorizontal,
    paddingRight:
      rightIcon && children
        ? sizeTokens.paddingIcon
        : sizeTokens.paddingHorizontal,
    paddingTop: sizeTokens.paddingVertical,
    paddingBottom: sizeTokens.paddingVertical,
    minHeight: rem(sizeTokens.minSize),
    minWidth: rem(sizeTokens.minSize),
    "> :not(:last-child)": {
      marginRight: `${spacing["space-1"].rem}rem`,
    },
    width: fullWidth ? "100%" : "fit-content",
  };
};
